import React from 'react'
import type { Block, Variable } from '../../editor/types'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui/select'

interface DropdownBlockProps {
  block: Block
  variableValues: Record<string, number | string | boolean>
  variables: Variable[]
  onSetVariableValue: (name: string, value: number | string | boolean) => void
}

interface DropdownOption {
  label: string
  value: string
}

type DropdownStyle = 'select' | 'radio' | 'cards' | 'chips'

export default function DropdownBlock({ block, variableValues, variables, onSetVariableValue }: DropdownBlockProps) {
  const varName = block.variable || ''
  const variable = variables.find((v) => v.name === varName)
  const currentValue = String(variableValues[varName] ?? variable?.default ?? '')
  const config = (block.config || {}) as Record<string, unknown>
  const options = (config.options as DropdownOption[]) || []
  const style = (config.variant as DropdownStyle) || 'select'
  const accent = block.accent || 'var(--accent, #5048e5)'

  const label = block.label && (
    <label className="text-sm font-semibold text-slate-900">{block.label}</label>
  )

  if (style === 'radio') {
    return (
      <div className="space-y-2">
        {label}
        <div className="space-y-1.5">
          {options.map((opt) => {
            const selected = currentValue === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => onSetVariableValue(varName, opt.value)}
                className="flex items-center gap-3 w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-slate-50 cursor-pointer"
              >
                <div
                  className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors"
                  style={{ borderColor: selected ? accent : '#cbd5e1' }}
                >
                  {selected && (
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: accent }} />
                  )}
                </div>
                <span className={`text-sm ${selected ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                  {opt.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  if (style === 'cards') {
    return (
      <div className="space-y-2">
        {label}
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(options.length, 3)}, 1fr)` }}>
          {options.map((opt) => {
            const selected = currentValue === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => onSetVariableValue(varName, opt.value)}
                className="px-4 py-3 rounded-xl text-center transition-all cursor-pointer hover:shadow-md"
                style={{
                  border: `2px solid ${selected ? accent : '#e2e8f0'}`,
                  backgroundColor: selected ? `color-mix(in srgb, ${accent} 8%, white)` : 'white',
                }}
              >
                <span
                  className="text-sm font-semibold"
                  style={{ color: selected ? accent : '#475569' }}
                >
                  {opt.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  if (style === 'chips') {
    return (
      <div className="space-y-2">
        {label}
        <div className="flex flex-wrap gap-2">
          {options.map((opt) => {
            const selected = currentValue === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => onSetVariableValue(varName, opt.value)}
                className="px-4 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer"
                style={{
                  backgroundColor: selected ? accent : '#f1f5f9',
                  color: selected ? 'white' : '#64748b',
                }}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  // Default: select dropdown
  return (
    <div className="space-y-2">
      {label}
      <Select value={currentValue} onValueChange={(val) => onSetVariableValue(varName, val)}>
        <SelectTrigger
          className="w-full bg-white border-slate-200 rounded-lg"
          style={{ '--tw-ring-color': accent } as React.CSSProperties}
        >
          <SelectValue placeholder="Select an option" />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
